'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useUserResourceSource } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AnonymousFallback, CardSkeleton, IslandKeyframes } from './_shared';

/**
 * Review submission card for a course. Pre-fills with the user's
 * existing review if any. Posts via
 * {@code UserResourceSource.reviews.submit}.
 */
export interface ReviewComposerProps {
  courseSlug: string;
  signInHref?: string;
}

interface Review {
  rating: number;
  text?: string;
  createdAt: string;
}

export function ReviewComposer({ courseSlug, signInHref }: ReviewComposerProps) {
  const { t } = useTranslation('site-builder-blocks');
  const user = useCurrentUser<{ name?: string }>();
  const ur = useUserResourceSource();
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [status, setStatus] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');

  const state = useAsyncResource<Review>(
    async (signal) => {
      if (!user || !ur?.reviews) return null;
      return await ur.reviews.fetchMine(courseSlug, signal);
    },
    [user, ur, courseSlug],
  );

  if (!user)
    return (
      <>
        <IslandKeyframes />
        <AnonymousFallback
          variant="card"
          prompt={t('islands.signInToReview')}
          {...(signInHref ? { signInHref } : {})}
        />
      </>
    );
  if (!ur?.reviews) return null;
  if (state.kind === 'loading')
    return (
      <>
        <IslandKeyframes />
        <CardSkeleton height={160} />
      </>
    );

  const existing = state.kind === 'loaded' ? state.data : null;
  const activeRating = rating || existing?.rating || 0;
  const activeText = text || existing?.text || '';

  const submit = async () => {
    if (status === 'busy' || activeRating === 0) return;
    setStatus('busy');
    try {
      const args: Parameters<NonNullable<typeof ur.reviews>['submit']>[0] = {
        courseSlug,
        rating: activeRating,
      };
      if (activeText) args.text = activeText;
      const ok = await ur.reviews!.submit(args);
      setStatus(ok ? 'done' : 'error');
    } catch (err) {
      // Surface the failure via the inline `'error'` status (rendered
      // as "Something went wrong." below the button — already wired
      // with role-implicit aria messaging via the status text). Log
      // the underlying error so it's not silently swallowed — without
      // this, every backend / network failure looked identical to a
      // typed "submit returned false".

      console.error('[ReviewComposer] review submission failed', err);
      setStatus('error');
    }
  };

  return (
    <div
      style={{
        padding: 'clamp(16px, 2.5vw, 24px)',
        background: 'var(--color-surface-card, #fff)',
        border: '1px solid var(--color-border, rgba(0,0,0,0.08))',
        borderRadius: 'var(--radius-md, 12px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ fontSize: 'var(--font-size-sm, 14px)', fontWeight: 600 }}>
        {existing ? t('islands.review.update') : t('islands.review.create')}
      </div>
      <div
        style={{ display: 'flex', gap: 4 }}
        role="radiogroup"
        aria-label={t('islands.review.rating')}
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={activeRating === n}
            onClick={() => setRating(n)}
            style={{
              fontSize: 'var(--font-size-2xl, 28px)',
              padding: 4,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: n <= activeRating ? '#fbbf24' : 'var(--color-border, #d4d4d8)',
            }}
          >
            ★
          </button>
        ))}
      </div>
      <textarea
        value={activeText}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder={t('islands.review.placeholder')}
        style={{
          padding: 'var(--space-2-5, 10px) var(--space-3, 12px)',
          border: '1px solid var(--color-border, rgba(0,0,0,0.12))',
          borderRadius: 6,
          fontSize: 'var(--font-size-sm, 14px)',
          fontFamily: 'var(--font-family-body, inherit)',
          background: 'var(--color-surface-card, #fff)',
          color: 'var(--color-text-primary, #18181b)',
          resize: 'vertical',
        }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          onClick={submit}
          disabled={status === 'busy' || activeRating === 0}
          style={{
            padding: 'var(--space-2, 8px) var(--space-4, 16px)',
            border: 'none',
            borderRadius: 8,
            background: 'var(--color-text-primary, #18181b)',
            color: 'var(--color-surface-card, #fff)',
            fontSize: '13px',
            fontWeight: 600,
            cursor: activeRating > 0 && status !== 'busy' ? 'pointer' : 'not-allowed',
            opacity: status === 'busy' || activeRating === 0 ? 0.5 : 1,
          }}
        >
          {status === 'busy'
            ? t('islands.review.submitting')
            : existing
              ? t('islands.review.updateAction')
              : t('islands.review.submitAction')}
        </button>
        {status === 'done' && (
          <span style={{ fontSize: '13px', color: 'var(--color-success-700, #047857)' }}>
            {t('islands.review.saved')}
          </span>
        )}
        {status === 'error' && (
          <span
            role="alert"
            aria-live="polite"
            style={{ fontSize: '13px', color: 'var(--color-danger, #dc2626)' }}
          >
            {t('islands.review.failed')}
          </span>
        )}
      </div>
    </div>
  );
}
